import PushPinIcon from '@mui/icons-material/PushPin';
import ReplyIcon from '@mui/icons-material/Reply';
import { Avatar, Box, IconButton, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { ROUTE_PATHS } from '../../routes/Routes';
import { Note } from '../../types/Note';
import { humanizeTimestamp } from '../../utility/dateUtils';
import AppLink from '../common/AppLink/AppLink';
import NoteMoreOptionsPopover from './NoteMoreOptionsPopover';
import AttachmentItem from './attachments/AttachmentItem';
import AttachmentPopover from './attachments/AttachmentPopover';
import {
  noteSummaryActionsStyles,
  noteSummaryEditedTextStyles,
  noteSummaryHeaderStyles,
  noteSummarySecondaryTextStyles,
  noteSummaryStyles,
  noteSummaryTextStyles,
  noteSummaryUserInfoStyles,
  noteSummaryUserStyles,
  pushPinIconStyles,
} from './noteStyles';
import { multilineTextSx } from '../../styles/sxStyling';

interface NoteSummaryProps {
  note: Note;
  currentUserEmail: string | undefined;
  onEdit: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onPin?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onReply?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const MAX_INLINE_ATTACHMENTS = 6;

const NoteSummary: React.FC<NoteSummaryProps> = ({ note, currentUserEmail, onEdit, onPin, onReply }) => {
  const inlineAttachments = _.take(note.attachment_data, MAX_INLINE_ATTACHMENTS);
  const remainingAttachments = _.drop(note.attachment_data, MAX_INLINE_ATTACHMENTS);
  const hasRemaining = remainingAttachments.length > 0;

  return (
    <Box sx={noteSummaryStyles}>
      <Box sx={noteSummaryUserStyles}>
        <Avatar src={note.employee_avatar_url} alt={note.employee_display_name} sx={{ width: 28, height: 28 }} />
        <Box sx={noteSummaryUserInfoStyles}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <AppLink to={`/${ROUTE_PATHS.EMPLOYEES}/${note.employee_uuid}`}>
              <Typography sx={noteSummaryHeaderStyles}>{note.employee_display_name}</Typography>
            </AppLink>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography sx={noteSummarySecondaryTextStyles}>{humanizeTimestamp(note.created_at)}</Typography>
              {note.edited_at && <Typography sx={noteSummaryEditedTextStyles}>edited</Typography>}
            </Box>
          </Box>
        </Box>
        <Box sx={noteSummaryActionsStyles}>
          {onPin && (
            <IconButton color="primary" onClick={onPin}>
              <PushPinIcon sx={pushPinIconStyles(!!note.pinned_at)} />
            </IconButton>
          )}
          {onReply && (
            <IconButton color="primary" onClick={onReply}>
              <ReplyIcon />
            </IconButton>
          )}
          {currentUserEmail === note.user_email && <NoteMoreOptionsPopover onEdit={onEdit} note={note} />}
        </Box>
      </Box>

      <Typography sx={{ ...multilineTextSx, ...noteSummaryTextStyles }}>{note.text}</Typography>

      {!_.isEmpty(inlineAttachments) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {inlineAttachments.map((attachment) => (
            <AttachmentItem key={attachment.key} attachment={attachment} />
          ))}
          {hasRemaining && <AttachmentPopover attachments={remainingAttachments} />}
        </Box>
      )}
    </Box>
  );
};

export default NoteSummary;
